import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

interface InspectionCheckpoint {
  id: string;
  name: string;
  type: "pass_fail" | "measurement" | "visual" | "checklist";
  required: boolean;
  criteria?: string;
  min?: number;
  max?: number;
  unit?: string;
  options?: string[];
}

interface Defect {
  code: string;
  description: string;
  severity: "minor" | "major" | "critical";
  location?: string;
  photoUrls?: string[];
}

interface InspectionData {
  productId: number | null;
  panelId: number | null;
  stage: string;
  inspectorId: string | null;
  checkpoints: Record<string, any>;
  defects: Defect[];
  photos: File[];
  notes: string;
  overallStatus: "pass" | "fail" | "rework";
}

const INSPECTION_STAGES = [
  { value: "pre_pour", label: "Pre-Pour", color: "#2196f3" },
  { value: "post_pour", label: "Post-Pour", color: "#ff9800" },
  { value: "post_strip", label: "Post-Strip", color: "#4caf50" },
  { value: "final", label: "Final", color: "#9c27b0" },
];

const DEFAULT_CHECKPOINTS: InspectionCheckpoint[] = [
  {
    id: "1",
    name: "Mold Condition",
    type: "pass_fail",
    required: true,
    criteria: "Mold clean, no damage, proper release agent applied",
  },
  {
    id: "2",
    name: "Rebar Placement",
    type: "pass_fail",
    required: true,
    criteria: "Rebar positioned according to specifications",
  },
  {
    id: "3",
    name: "Panel Thickness",
    type: "measurement",
    required: true,
    min: 5.75,
    max: 6.25,
    unit: "inches",
  },
  {
    id: "4",
    name: "Surface Finish",
    type: "visual",
    required: true,
    criteria: "Smooth, no honeycombing, no exposed aggregate",
  },
  {
    id: "5",
    name: "Dimensions Check",
    type: "checklist",
    required: true,
    options: ["Length within tolerance", "Width within tolerance", "Square corners", "Proper chamfers"],
  },
];

class QCInspectionWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  inspectionData: InspectionData = {
    productId: null,
    panelId: null,
    stage: "pre_pour",
    inspectorId: null,
    checkpoints: {},
    defects: [],
    photos: [],
    notes: "",
    overallStatus: "pass",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.initializeInspector();
  }

  // Actions
  initializeInspector = () => {
    const userStore = this.rootStore.userStore as ServiceStore;
    const currentUser = (userStore as any)?.currentUser;
    if (currentUser?.id) {
      this.inspectionData.inspectorId = currentUser.id;
    }
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateInspectionData = <K extends keyof InspectionData>(
    field: K,
    value: InspectionData[K]
  ) => {
    this.inspectionData[field] = value;
  };

  selectPanel = (panelId: number) => {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    const panel = panelStore?.entities.find((p: any) => p.id === panelId);

    runInAction(() => {
      this.inspectionData.panelId = panelId;
      this.inspectionData.productId = panel?.productId || null;
    });
  };

  updateCheckpoint = (checkpointId: string, value: any) => {
    this.inspectionData.checkpoints[checkpointId] = value;
  };

  addDefect = () => {
    this.inspectionData.defects.push({
      code: "",
      description: "",
      severity: "minor",
      location: "",
      photoUrls: [],
    });
  };

  updateDefect = (index: number, field: keyof Defect, value: any) => {
    if (this.inspectionData.defects[index]) {
      this.inspectionData.defects[index][field] = value as any;
    }
  };

  removeDefect = (index: number) => {
    this.inspectionData.defects = this.inspectionData.defects.filter((_, i) => i !== index);
  };

  addPhotos = (files: FileList) => {
    const newFiles = Array.from(files);
    this.inspectionData.photos = [...this.inspectionData.photos, ...newFiles];
  };

  removePhoto = (index: number) => {
    this.inspectionData.photos = this.inspectionData.photos.filter((_, i) => i !== index);
  };

  updateNotes = (notes: string) => {
    this.inspectionData.notes = notes;
  };

  // Async actions
  calculateOverallStatus = (): "pass" | "fail" | "rework" => {
    // Check if any required checkpoints failed
    const hasFailures = Object.entries(this.inspectionData.checkpoints).some(([id, value]) => {
      const checkpoint = DEFAULT_CHECKPOINTS.find((cp) => cp.id === id);
      if (!checkpoint?.required) return false;

      if (checkpoint.type === "pass_fail" && value === "fail") return true;
      if (checkpoint.type === "measurement") {
        const numValue = parseFloat(value);
        if (checkpoint.min && numValue < checkpoint.min) return true;
        if (checkpoint.max && numValue > checkpoint.max) return true;
      }
      return false;
    });

    // Check for critical defects
    const hasCriticalDefects = this.inspectionData.defects.some((d) => d.severity === "critical");

    if (hasCriticalDefects || hasFailures) {
      return "fail";
    } else if (this.inspectionData.defects.some((d) => d.severity === "major")) {
      return "rework";
    } else {
      return "pass";
    }
  };

  submitInspection = async () => {
    this.loading = true;
    this.error = null;

    try {
      const qcStore = this.rootStore.qcStore as ServiceStore;
      const panelStore = this.rootStore.panelStore as ServiceStore;

      const overallStatus = this.calculateOverallStatus();

      // Create QC record
      const qcRecord = await qcStore?.create({
        productId: this.inspectionData.productId,
        productionUnitId: this.inspectionData.panelId,
        inspectionDateTime: new Date().toISOString(),
        inspectorId: this.inspectionData.inspectorId,
        status: overallStatus,
        notes: this.inspectionData.notes,
        defects: this.inspectionData.defects.length > 0 ? this.inspectionData.defects : null,
        // TODO: Upload photos and get URLs
        checklistData: {
          stage: this.inspectionData.stage,
          checkpoints: this.inspectionData.checkpoints,
        },
      });

      // Update panel QC status
      if (this.inspectionData.panelId) {
        await panelStore?.patch(this.inspectionData.panelId, {
          qcStatus: overallStatus,
          lastQcDate: new Date().toISOString(),
        });
      }

      runInAction(() => {
        this.success = "QC inspection submitted successfully!";
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to submit inspection";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.inspectionData = {
      productId: null,
      panelId: null,
      stage: "pre_pour",
      inspectorId: (this.rootStore.userStore as any)?.currentUser?.id || null,
      checkpoints: {},
      defects: [],
      photos: [],
      notes: "",
      overallStatus: "pass",
    };
  };

  // Computed values
  get panels() {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    return panelStore?.entities || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get availableCheckpoints() {
    return DEFAULT_CHECKPOINTS;
  }

  get inspectionStages() {
    return INSPECTION_STAGES;
  }

  get selectedPanel() {
    if (!this.inspectionData.panelId) return null;
    return this.panels.find((p: any) => p.id === this.inspectionData.panelId);
  }

  get selectedProduct() {
    if (!this.inspectionData.productId) return null;
    return this.products.find((p: any) => p.id === this.inspectionData.productId);
  }
}

export default QCInspectionWizardStore;